import React, { useEffect, useMemo, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Button, Col, Form, Input, Row, Select, message } from "antd";
import InputMask from "react-input-mask";
import { useDispatch } from "react-redux";
import { useMediaQuery } from "react-responsive";
import { postUserQuickSignUp } from "../redux/AuthController";
import "../Register/Register.css";
import "../Register/SignUpPages/SignupPages.css";
import fertilityImage from "../../assets/images/auth/fertilityImage.svg";
import "./QuickStartSignup.css";

const phoneRegex = /^\+1-\d{3}-\d{3}-\d{4}$/;
const normalizePhone = (value) => {
  const v = String(value || "")
    // Normalize unicode dashes to hyphen
    .replace(/[\u2010\u2011\u2012\u2013\u2014\u2212]/g, "-")
    // Remove spaces around separators
    .replace(/\s+/g, "")
    .trim();

  // Autofill can hand us a complete number without the mask's dashes
  const digits = v.replace(/\D/g, "");
  const national =
    digits.length === 11 && digits.startsWith("1")
      ? digits.slice(1)
      : digits.length === 10 && !v.startsWith("+")
        ? digits
        : null;
  return national
    ? `+1-${national.slice(0, 3)}-${national.slice(3, 6)}-${national.slice(6)}`
    : v;
};
const uniformButtonStyle = {
  height: "46px",
  borderRadius: "8px",
  fontWeight: 600,
  fontSize: "16px",
  fontFamily: "Montserrat, sans-serif",
  color: "#ffffff",
  backgroundColor: "#00ADEF",
  border: "1px solid #00ADEF",
  boxShadow: "0 4px 10px rgba(0, 173, 239, 0.25)",
};

export default function QuickStartSignup() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const isMobile = useMediaQuery({ maxWidth: 767 });

  useEffect(() => {
    const hasConfirmation = !!localStorage.getItem("quickBookingConfirmation");
    if (hasConfirmation) {
      // Previous quick booking already went through - starting Quick Start again means a new booking, so drop all of that state.
      localStorage.removeItem("quickPatientDraft");
      localStorage.removeItem("quickBookingConfirmation");
      localStorage.removeItem("quickBookingProvider");
      return;
    }

    const hasDraft = !!localStorage.getItem("quickPatientDraft");
    if (hasDraft) {
      // Signed up but haven't finished booking - skip straight to clinician selection 
      navigate("/quick-book", { replace: true });
    }
  }, [navigate]);

  const sexOptions = useMemo(
    () => [
      { label: "Female", value: "Female" },
      { label: "Male", value: "Male" },
      { label: "Intersex", value: "Intersex" },
      { label: "Prefer not to say", value: "Prefer not to say" },
    ],
    []
  );

  const locationOptions = useMemo(
    () => [
      { label: "Calgary", value: "Calgary" },
      { label: "Edmonton", value: "Edmonton" }
    ],
    []
  );

  const onFinish = async (values) => {
    const phone = normalizePhone(values.phone);
    const payload = {
      FirstName: values.firstName.trim(),
      LastName: values.lastName.trim(),
      Email: values.email.trim(),
      PhoneNumber: phone,
      Gender: values.sexAtBirth,
      RefSource: (values.heardAboutUs || "").trim(),
      Location: values.location,
    };

    setSubmitting(true);
    try {
      const result = await dispatch(postUserQuickSignUp(payload));
      if (postUserQuickSignUp.rejected.match(result)) {
        const err = result.payload;
        const msg =
          (typeof err === "object" && err?.message) ||
          (Array.isArray(err?.errors) && err.errors[0]) ||
          "Quick sign up failed. Please try again.";
        message.error(String(msg));
        return;
      }

      const data = result.payload;
      message.success(data?.message || "You're signed up. Continue to book your appointment.");

      const draft = {
        firstName: payload.FirstName,
        lastName: payload.LastName,
        sexAtBirth: payload.Gender,
        phone: values.phone,
        email: payload.Email,
        heardAboutUs: payload.RefSource,
        location: payload.Location,
        createdAt: new Date().toISOString(),
        quickSignUpResponse: data,
      };

      localStorage.setItem("quickPatientDraft", JSON.stringify(draft));
      localStorage.removeItem("quickBookingConfirmation");
      
      navigate("/quick-book");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="">
      <div className="full-width-container">
        <Row gutter={24} style={{ height: "100vh" }}>
          <Col span={2} className="column-1" style={{ backgroundColor: "#EFD0BD" }} />
          <Col span={20} className="column-2">
            <div className="form-scrollable">
              <div className="col-xl-9 col-lg-10 col-md-12">
                <nav className="navbar mt-3">
                  <img
                    className="float-left"
                    src={fertilityImage}
                    alt="loginImage"
                    style={{ width: "150px" }}
                  />
                  <form className="d-flex" role="search">
                    <NavLink
                      to="/"
                      className="btn-user btn-block"
                      style={{
                        ...uniformButtonStyle,
                        minWidth: "126px",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "0 20px",
                        lineHeight: 1,
                      }}
                    >
                      <span>Sign In</span>
                    </NavLink>
                  </form>
                </nav>

                <div className="card o-hidden border-0 my-3">
                  <div className="card-body p-5">
                    <div className="row">
                      <div className="col-lg-12">
                        <p
                          style={{
                            fontSize: 20,
                            fontWeight: 700,
                            color: "#000",
                            textAlign: "center",
                            marginBottom: 6,
                          }}
                        >
                          Quick Start
                        </p>
                        <p style={{ textAlign: "center", color: "#6c757d", marginBottom: 20 }}>
                          Book your first appointment without creating a full profile or paying upfront.
                        </p>

                        <Form
                          className="quick-start-signup-form"
                          form={form}
                          layout="vertical"
                          onFinish={onFinish}
                          initialValues={{ sexAtBirth: "Female" }}
                        >
                          <Row gutter={16}>
                            <Col xs={24} md={12}>
                              <Form.Item
                                name="firstName"
                                label="First Name"
                                rules={[
                                  { required: true, message: "Please enter your first name" },
                                  { whitespace: true, message: "First name cannot be empty" },
                                ]}
                              >
                                <Input placeholder="Jane" autoComplete="given-name" />
                              </Form.Item>
                            </Col>

                            <Col xs={24} md={12}>
                              <Form.Item
                                name="lastName"
                                label="Last Name"
                                rules={[
                                  { required: true, message: "Please enter your last name" },
                                  { whitespace: true, message: "Last name cannot be empty" },
                                ]}
                              >
                                <Input placeholder="Doe" autoComplete="family-name" />
                              </Form.Item>
                            </Col>
                          </Row>

                          <Row gutter={16}>
                            <Col xs={24} md={12}>
                              <Form.Item
                                name="sexAtBirth"
                                label="Sex at Birth"
                                rules={[{ required: true, message: "Please select sex at birth" }]}
                              >
                                <Select options={sexOptions} />
                              </Form.Item>
                            </Col>

                            <Col xs={24} md={12}>
                              <Form.Item
                                name="phone"
                                label="Phone"
                                validateTrigger={["onChange", "onBlur", "onSubmit"]}
                                validateDebounce={250}
                                getValueFromEvent={(e) => e?.target?.value}
                                normalize={(v) => normalizePhone(v)}
                                rules={[
                                  { required: true, message: "Please enter your phone number" },
                                  {
                                    validator: (_, value) => {
                                      const v = normalizePhone(value);
                                      if (!v) return Promise.resolve();
                                      if (phoneRegex.test(v)) return Promise.resolve();
                                      return Promise.reject(
                                        new Error("Use +1-XXX-XXX-XXXX format (e.g. +1-416-555-1234)"),
                                      );
                                    },
                                  },
                                ]}
                              >
                                <InputMask mask="+1-999-999-9999" maskChar={null}>
                                  {(inputProps) => (
                                    <Input
                                      {...inputProps}
                                      placeholder="+1-416-555-1234"
                                      autoComplete="tel"
                                    />
                                  )}
                                </InputMask>
                              </Form.Item>
                            </Col>
                          </Row>

                          <Row gutter={16}>
                            <Col xs={24} md={12}>
                              <Form.Item
                                name="email"
                                label="Email"
                                rules={[
                                  { required: true, message: "Please enter your email" },
                                  { type: "email", message: "Please enter a valid email address" },
                                ]}
                              >
                                <Input placeholder="jane@example.com" autoComplete="email" />
                              </Form.Item>
                            </Col>

                            <Col xs={24} md={12}>
                              <Form.Item
                                name="heardAboutUs"
                                label="How did you hear about us?"
                                rules={[
                                  {
                                    required: true,
                                    message: "Please select or enter how you heard about us",
                                  },
                                ]}
                              >
                                <Input placeholder="Friend, Google, Instagram, etc." />
                              </Form.Item>
                            </Col>
                          </Row>

                          <Row gutter={16}>
                            <Col xs={24} md={12}>
                              <Form.Item
                                name="location"
                                label="Location"
                                rules={[{ required: true, message: "Please select a location" }]}
                              >
                                <Select placeholder="Select a location" options={locationOptions} />
                              </Form.Item>
                            </Col>
                          </Row>

                          <Row justify="end" style={{ marginTop: 18 }}>
                            <Button
                              type="primary"
                              htmlType="submit"
                              loading={submitting}
                              disabled={submitting}
                              style={{
                                width: isMobile ? "" : "200px",
                                ...uniformButtonStyle,
                                float: "right",
                              }}
                            >
                              Book Now
                            </Button>
                          </Row>

                          <div style={{ marginTop: 14 }}>
                            <small style={{ color: "#6c757d" }}>
                              By continuing, you agree we may contact you about your appointment.
                            </small>
                          </div>
                        </Form>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Col>
          <Col span={2} className="column-3" style={{ backgroundColor: "#EFD0BD" }} />
        </Row>
      </div>
    </div>
  );
}

